import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useLessonQuiz from "./useLessonQuiz";
import QuestionCard from "./QuestionCard";
import QuizResult from "./QuizResult";

// Is a single question fully answered? (matching needs every left assigned)
const isAnswered = (q, val) => {
  if (q.type === "matching")
    return (q.lefts || []).every((l) => val && val[l._id]);
  return val !== undefined && val !== null && val !== "";
};

// Quiz modal, used for both the lesson quiz (on lesson end) and the course
// "final" quiz (after all lessons). Optional — the user can Skip.
// `onLoaded(hasQuiz)` lets the watch page know a quiz exists; `onFinish` closes it.
const LessonQuiz = ({
  scope = "lesson",
  courseId,
  lessonId,
  token,
  profileId,
  open,
  onFinish,
  onLoaded,
}) => {
  const { t } = useTranslation();
  const { quiz, loading, result, submitting, error, submit, reset } =
    useLessonQuiz({ scope, courseId, lessonId, token, profileId });
  const [answers, setAnswers] = useState({});

  const hasQuiz = !!(quiz && quiz.questions?.length);

  useEffect(() => setAnswers({}), [scope, lessonId]);
  useEffect(() => onLoaded?.(hasQuiz), [hasQuiz, onLoaded]);

  if (!open || loading || !hasQuiz) return null;

  const setAnswer = (qid, value) =>
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  const allAnswered = quiz.questions.every((q) => isAnswered(q, answers[q._id]));
  const correctById = result
    ? Object.fromEntries(result.results.map((r) => [String(r.questionId), r.correct]))
    : {};
  const retake = () => {
    setAnswers({});
    reset();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80">
      <div className="bg-grey border border-primary/40 rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto video-series-scrollbar shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-grey/95 backdrop-blur px-5 sm:px-6 py-4 border-b border-lightGrey flex items-center gap-3 z-10">
          <span className="text-xs font-bold tracking-wide bg-primary text-white px-3 py-1 rounded-full uppercase">
            {scope === "course" ? t("quiz.final_title") : t("quiz.title")}
          </span>
          <p className="text-lightWhite text-sm truncate">
            {quiz.title || t("quiz.subtitle")}
          </p>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-5">
          {result && (
            <div className="mb-5">
              <QuizResult result={result} />
            </div>
          )}

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-primary/10 border border-primary/40 text-white text-sm">
              {typeof error === "string" && error !== "submit_failed"
                ? error
                : t("quiz.submit_error")}
            </div>
          )}

          <div className="flex flex-col gap-4">
            {quiz.questions.map((q, i) => (
              <QuestionCard
                key={q._id}
                index={i}
                question={q}
                value={answers[q._id]}
                onChange={(v) => setAnswer(q._id, v)}
                review={!!result}
                correct={correctById[String(q._id)]}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-grey/95 backdrop-blur px-5 sm:px-6 py-4 border-t border-lightGrey flex flex-wrap items-center justify-between gap-3">
          {!result ? (
            <>
              <button
                type="button"
                onClick={onFinish}
                className="px-5 py-2.5 rounded-lg border border-lightGrey text-lightWhite hover:text-white hover:border-white transition-colors"
              >
                {t("quiz.skip")}
              </button>
              <div className="flex items-center gap-3">
                {!allAnswered && (
                  <span className="text-xs text-lightWhite">
                    {t("quiz.answer_all")}
                  </span>
                )}
                <button
                  type="button"
                  disabled={!allAnswered || submitting}
                  onClick={() => submit(answers)}
                  className="px-6 py-2.5 rounded-lg bg-primary text-white font-medium disabled:opacity-50 hover:bg-darkPrimary transition-colors"
                >
                  {t("quiz.submit")}
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={retake}
                className="px-5 py-2.5 rounded-lg border border-lightGrey text-white hover:border-primary transition-colors"
              >
                {t("quiz.retake")}
              </button>
              <button
                type="button"
                onClick={onFinish}
                className="px-6 py-2.5 rounded-lg bg-primary text-white font-medium hover:bg-darkPrimary transition-colors"
              >
                {scope === "course" ? t("quiz.done") : t("quiz.next_lesson")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonQuiz;
